<template>
    <div
        class="rounded-muval-2 flex min-h-[62px] w-full flex-col justify-between gap-3 border bg-white p-3 md:flex-row md:items-center md:gap-0"
    >
        <MatchFleetInfo :match="match" />

        <div class="flex flex-col items-center gap-x-5 border-t md:flex-row md:border-t-0">
            <div class="grid w-full grid-cols-2 py-3 md:flex md:w-auto md:gap-4 md:py-0">
                <div
                    v-if="hasCallOutFee"
                    class="flex flex-col md:border-l md:border-black/10 md:py-1 md:pl-4"
                >
                    <span class="text-muval-gray-3 text-xs font-medium">Call-out fee</span>
                    <span class="text-muval-gray-1 text-sm font-medium">
                        {{
                            callOutFeeAsTime && match.call_out_fee_time ?
                                match.call_out_fee_time
                            :   match.call_out_fee?.formatted
                        }}
                    </span>
                </div>

                <div
                    v-if="!hasCallOutFee"
                    class="hidden h-[41px] w-px bg-black/10 md:block"
                />

                <div class="flex flex-col justify-center">
                    <span
                        v-if="hasCallOutFee"
                        class="text-muval-gray-3 text-xs font-medium"
                        >From</span
                    >
                    <span class="text-sm font-medium text-black">
                        <template v-if="isVariable">
                            {{ formattedVariablePrice }}
                        </template>
                        <template v-else>
                            {{ match.estimated_total?.formatted }}
                        </template>
                    </span>
                </div>
            </div>

            <MuvalButton
                :tertiary="readonly"
                class="w-full font-medium md:w-auto md:px-8"
                :loading="
                    (!match.isFixed && match.selecting) ||
                    (hasOnlyOneOption && !match.hasOptions && match.selecting) ||
                    (hasOnlyOneOption && match.hasOptions && match.options[0].selecting)
                "
                :icon="readonly ? 'system-check' : ''"
                icon-right
                @click="handleMatchSelect"
                >{{ readonly ? 'Selected' : 'Select' }}</MuvalButton
            >
        </div>
    </div>
</template>
<script setup lang="ts">
import type { MoveJob, MoveMatch } from '@/types';
import { useResultsStore, useAppStore } from '@/store';

const results = useResultsStore();
const appStore = useAppStore();
const router = useRouter();

const isPerHourPricing = computed(() => appStore.tenantPerHourPricing);
const callOutFeeAsTime = computed(() => appStore.tenantCallOutFeeAsTime);

interface Props {
    match: MoveMatch;
    moveJob?: MoveJob;
    readonly: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
});

const hasCallOutFee = computed(() => {
    // A fixed price holds no call-out fee, even where the quote kept an amount for a switch back.
    if (props.match?.pricing_type === 'FIXED') return false;
    const amount = props.match.call_out_fee?.amount;
    if (!amount) return false;
    return Number(amount) > 0;
});

const formattedVariablePrice = computed(() => {
    const pricePerUnit = props.match.price_per_unit;
    if (!pricePerUnit?.formatted) return '-';

    if (isPerHourPricing.value) {
        return `${pricePerUnit.formatted}/hour`;
    }

    return `${pricePerUnit.formatted}/30 mins`;
});

const hasOnlyOneOption = computed(() => {
    if (props.match?.isLocal) {
        if (props.match?.hasOptions && props.match?.options?.length >= 1) {
            if (props.match?.options?.length > 1) {
                console.log('match has more than one option');
                console.log(props.match?.company?.name);
            }

            return true;
        }
    }

    if (props.match?.hasOptions && props.match?.options?.length > 1) {
        return false;
    } else {
        return true;
    }
});

const canSelectMatch = computed(() => {
    // If the user is on the review page, prevent selecting the match again.
    if (isReviewPage.value || router.currentRoute.value.name === 'Confirmed') {
        return false;
    }

    if (props.match.selecting) {
        return false;
    }

    if (!hasOnlyOneOption.value) {
        return false;
    }

    return true;
});

const isReviewPage = computed(() => {
    return router.currentRoute.value.name === 'Review';
});

const handleMatchSelect = async () => {
    console.log('Can select match', canSelectMatch.value);

    if (!canSelectMatch.value || props.readonly) {
        return;
    }

    if (props.match.hasOptions) {
        console.log('props.match.options[0]');
        results.selectMatch(props.match.options[0]);
    } else {
        console.log('props.match');
        results.selectMatch(props.match);
    }
};

const isVariable = computed(() => {
    return props.match?.pricing_type === 'VARIABLE';
});
</script>
